/**
 * AutoAds — нормалізація ассетів оголошення.
 *
 * ЧОМУ ЦЕ ПОТРІБНО
 * ----------------
 * У базі співіснують ДВА формати оголошень:
 *
 *   [A] LEGACY (одно-промптовий шлях, STRATEGY_SYSTEM_PROMPT):
 *       ad_group.ads = [{ variant, focus, headline_1..3, description_1..2 }]
 *       Максимум 3 заголовки + 2 описи — епоха ETA.
 *
 *   [B] MULTI-STAGE (поточний прод, MULTI_STAGE_CHAIN=true):
 *       ad_group.headlines        = ["...", ...]            до 15 рядків
 *       ad_group.descriptions     = ["...", ...]            до 4 рядків
 *       ad_group.headlines_api    = [{ text, pinned_position, part, position }]
 *       ad_group.descriptions_api = [{ text, angle, position }]
 *       Поля `ads` НЕ ІСНУЄ ВЗАГАЛІ.
 *
 * БАГ, ЯКИЙ ЦЕ ЛІКУЄ: UI читав лише `group.ads`, тому при увімкненому
 * MULTI_STAGE_CHAIN оголошення не рендерились ніде — ні у Стратегії, ні в
 * LaunchWizard («Заголовки RSA (0 шт)»). Тексти генерувались і зберігались,
 * але користувач їх не бачив.
 *
 * ПРАВИЛО: весь UI читає ассети ЛИШЕ через ці хелпери. Тоді обидва формати
 * рендеряться коректно, і старі стратегії в базі не ламаються.
 */

// Офіційні жорсткі ліміти Google Ads
export const LIMIT_HEADLINE = 30;
export const LIMIT_DESCRIPTION = 90;
export const RSA_MAX_HEADLINES = 15;
export const RSA_MAX_DESCRIPTIONS = 4;

// Оптимальні (НЕ максимальні) довжини — Optmyzr, ~20 тис. акаунтів, 06.04.2026.
// Заголовки <20 симв.: CPA $9.35 проти $18.27 у 21–30 симв.
// Описи 61–70 симв.: CPA $11.49 проти $20.11 у 81–90 симв.
export const HEADLINE_OPTIMAL_MAX = 20;
export const DESCRIPTION_OPTIMAL_MAX = 70;

const LEGACY_HEADLINE_KEYS = ["headline_1", "headline_2", "headline_3"];
const LEGACY_DESCRIPTION_KEYS = ["description_1", "description_2"];

const isNonEmptyStr = (v) => typeof v === "string" && v.trim().length > 0;

/** Витягує текст із рядка або з об'єкта {text}. */
function asText(v) {
    if (isNonEmptyStr(v)) return v;
    if (v && typeof v === "object" && isNonEmptyStr(v.text)) return v.text;
    return null;
}

/** Заголовки. Розуміє: масив рядків, масив {text}, legacy headline_1..3. */
export function getAdHeadlines(ad) {
    if (!ad) return [];
    if (Array.isArray(ad.headlines_api) && ad.headlines_api.length) {
        return ad.headlines_api.map(asText).filter(Boolean);
    }
    if (Array.isArray(ad.headlines)) {
        return ad.headlines.map(asText).filter(Boolean);
    }
    return LEGACY_HEADLINE_KEYS.map((k) => ad[k]).filter(isNonEmptyStr);
}

/** Описи. Розуміє: масив рядків, масив {text}, legacy description_1..2. */
export function getAdDescriptions(ad) {
    if (!ad) return [];
    if (Array.isArray(ad.descriptions_api) && ad.descriptions_api.length) {
        return ad.descriptions_api.map(asText).filter(Boolean);
    }
    if (Array.isArray(ad.descriptions)) {
        return ad.descriptions.map(asText).filter(Boolean);
    }
    return LEGACY_DESCRIPTION_KEYS.map((k) => ad[k]).filter(isNonEmptyStr);
}

/** true, якщо ассети у новому RSA-форматі (масиви, а не headline_1..3). */
export function isRsaFormat(ad) {
    return (
        Array.isArray(ad?.headlines) ||
        Array.isArray(ad?.headlines_api) ||
        Array.isArray(ad?.descriptions) ||
        Array.isArray(ad?.descriptions_api)
    );
}

/**
 * ГОЛОВНИЙ ХЕЛПЕР — список оголошень для рендеру, з ad_group будь-якого формату.
 *
 * Legacy      → повертає group.ads як є.
 * Multi-stage → синтезує ОДНЕ оголошення RSA з group.headlines/descriptions,
 *               бо в RSA немає «варіантів»: одна група = одне RSA з пулом
 *               заголовків, які Google міксує сам.
 * Порожньо    → [].
 */
export function getGroupAds(group) {
    if (!group) return [];

    if (Array.isArray(group.ads) && group.ads.length > 0) {
        return group.ads;
    }

    const headlines = getAdHeadlines(group);
    const descriptions = getAdDescriptions(group);
    if (headlines.length === 0 && descriptions.length === 0) return [];

    return [
        {
            variant: "RSA",
            focus: "Responsive Search Ad — Google міксує заголовки автоматично",
            headlines,
            descriptions,
            headlines_api: Array.isArray(group.headlines_api) ? group.headlines_api : null,
            descriptions_api: Array.isArray(group.descriptions_api) ? group.descriptions_api : null,
            _synthesized: true,
        },
    ];
}

/**
 * Мапа закріплень: { [індекс заголовка]: позиція 1|2|3 }.
 * Читається з headlines_api[].pinned_position (Stage 4 → mapper).
 * Часткове закріплення — правильний патерн: CPA $13.68 проти $32.57
 * у повністю закріплених оголошеннях (Optmyzr 2026).
 */
export function getHeadlinePins(ad) {
    const api = ad?.headlines_api;
    if (!Array.isArray(api)) return {};
    const pins = {};
    api.forEach((h, i) => {
        const p = h?.pinned_position;
        if (p === 1 || p === 2 || p === 3) pins[i] = p;
    });
    return pins;
}

/** Скільки заголовків закріплено. Повне закріплення — антипатерн. */
export function countPinned(ad) {
    return Object.keys(getHeadlinePins(ad)).length;
}

/** Роль заголовка (keyword_match | value_proposition | cta_trust) або null. */
export function getHeadlineParts(ad) {
    const api = ad?.headlines_api;
    if (!Array.isArray(api)) return {};
    const parts = {};
    api.forEach((h, i) => {
        if (isNonEmptyStr(h?.part)) parts[i] = h.part;
        else if (isNonEmptyStr(h?.category)) parts[i] = h.category;
    });
    return parts;
}

/** Індекс заголовка, закріпленого на позицію 1 (або null). Legacy-сумісність. */
export function getPinnedHeadlineIndex(ad) {
    const pins = getHeadlinePins(ad);
    const found = Object.keys(pins).find((i) => pins[i] === 1);
    if (found !== undefined) return Number(found);
    const v = ad?.pinning?.headline_position_1;
    return Number.isInteger(v) ? v : null;
}

/**
 * Статус довжини для підсвітки в UI.
 *   "over" — перевищено офіційний ліміт Google (червоне, блокує)
 *   "long" — у межах ліміту, але довше за оптимум за даними Optmyzr (жовте)
 *   "ok"   — оптимально
 */
export function lengthStatus(text, kind = "headline") {
    const len = (text || "").length;
    const hard = kind === "headline" ? LIMIT_HEADLINE : LIMIT_DESCRIPTION;
    const soft = kind === "headline" ? HEADLINE_OPTIMAL_MAX : DESCRIPTION_OPTIMAL_MAX;
    if (len > hard) return "over";
    if (len > soft) return "long";
    return "ok";
}

/** Плоский унікальний список заголовків з групи (обидва формати) — для LaunchWizard. */
export function collectGroupHeadlines(group) {
    const all = getGroupAds(group).flatMap(getAdHeadlines);
    return [...new Set(all)];
}

/** Плоский унікальний список описів з групи (обидва формати) — для LaunchWizard. */
export function collectGroupDescriptions(group) {
    const all = getGroupAds(group).flatMap(getAdDescriptions);
    return [...new Set(all)];
}

/** Плоский унікальний список заголовків зі списку оголошень. */
export function collectHeadlines(ads) {
    return [...new Set((ads || []).flatMap(getAdHeadlines))];
}

/** Плоский унікальний список описів зі списку оголошень. */
export function collectDescriptions(ads) {
    return [...new Set((ads || []).flatMap(getAdDescriptions))];
}

/** Текст «скопіювати все» для одного оголошення. */
export function adAsPlainText(ad) {
    const h = getAdHeadlines(ad).map((t) => `H: ${t}`);
    const d = getAdDescriptions(ad).map((t) => `D: ${t}`);
    return [...h, ...d].join("\n");
}

/* ════════════════════════════════════════════════════════════════════════
   КЛЮЧОВІ СЛОВА — той самий клас проблеми, що й з оголошеннями.

   [A] LEGACY / API-формат: [{ text, match_type }]   match_type: exact|phrase|broad
   [B] MULTI-STAGE (прод):  ["[buty]", "\"buty damskie\"", "buty"]
       mapper вже обгорнув їх у синтаксис Google через _format_keyword_display.

   LaunchWizard читав лише k.text — на рядках це давало undefined → порожні
   рядки замість ключових слів. Ці хелпери приймають обидва формати.
   ══════════════════════════════════════════════════════════════════════ */

/** Знімає обгортки [..] та ".." з ключового слова. */
export function stripKeywordWrappers(raw) {
    return String(raw ?? "")
        .trim()
        .replace(/^\[(.*)\]$/, "$1")
        .replace(/^"(.*)"$/, "$1")
        .trim();
}

/**
 * Ключове слово у синтаксисі Google Ads: [точна] · "фраза" · широка.
 * Рядок, який mapper уже обгорнув, повертається як є.
 */
export function formatKeyword(kw) {
    if (isNonEmptyStr(kw)) return kw.trim();
    if (!kw || typeof kw !== "object") return "";
    const clean = stripKeywordWrappers(kw.text);
    if (!clean) return "";
    const mt = String(kw.match_type || "").toLowerCase();
    if (mt === "exact") return `[${clean}]`;
    if (mt === "phrase") return `"${clean}"`;
    return clean;
}

/** Ключові слова групи у синтаксисі Google, без порожніх. Обидва формати. */
export function getGroupKeywords(group) {
    const src = Array.isArray(group?.keywords_api) && group.keywords_api.length
        ? group.keywords_api
        : (group?.keywords || []);
    return src.map(formatKeyword).filter((k) => k.length > 0);
}

/** Мінус-слова групи як чистий текст, без провідного «-». Обидва формати. */
export function getGroupNegatives(group) {
    const src = Array.isArray(group?.negative_keywords_api) && group.negative_keywords_api.length
        ? group.negative_keywords_api
        : (group?.negative_keywords || []);
    return src
        .map((n) => (isNonEmptyStr(n) ? n : n?.text))
        .filter(isNonEmptyStr)
        .map((t) => stripKeywordWrappers(String(t).replace(/^-/, "")))
        .filter((t) => t.length > 0);
}

/** Тип відповідності ключового слова. Виводиться з обгортки, якщо це рядок. */
export function getKeywordMatchType(kw) {
    if (kw && typeof kw === "object" && isNonEmptyStr(kw.match_type)) {
        return String(kw.match_type).toLowerCase();
    }
    const raw = isNonEmptyStr(kw) ? kw.trim() : String(kw?.text ?? "").trim();
    if (raw.startsWith("[") && raw.endsWith("]")) return "exact";
    if (raw.startsWith('"') && raw.endsWith('"')) return "phrase";
    return "broad";
}

/**
 * Ключові слова групи з повними метаданими — для чіпів та підказок.
 * Повертає [{ display, clean, match_type, intent, variation_type, priority }].
 * Працює і на рядках з mapper, і на об'єктах з keywords_api.
 */
export function getGroupKeywordsDetailed(group) {
    const src = Array.isArray(group?.keywords_api) && group.keywords_api.length
        ? group.keywords_api
        : (group?.keywords || []);
    return src
        .map((kw) => {
            const isObj = kw && typeof kw === "object";
            const clean = stripKeywordWrappers(isObj ? kw.text : kw);
            if (!clean) return null;
            const mt = getKeywordMatchType(kw);
            const display = mt === "exact" ? `[${clean}]` : mt === "phrase" ? `"${clean}"` : clean;
            return {
                display,
                clean,
                match_type: mt,
                intent: isObj ? kw.intent || null : null,
                variation_type: isObj ? kw.variation_type || null : null,
                priority: isObj ? kw.priority ?? null : null,
            };
        })
        .filter(Boolean);
}

/**
 * Розподіл типів відповідності: { exact, phrase, broad, total }.
 * Спершу бере готовий match_type_distribution від бекенду (у відсотках),
 * інакше рахує з самих ключових слів.
 */
export function getMatchTypeCounts(group) {
    const kws = getGroupKeywordsDetailed(group);
    const total = kws.length;
    const counts = { exact: 0, phrase: 0, broad: 0, total };

    const dist = group?.match_type_distribution;
    if (dist && total > 0) {
        const e = Number(dist.exact_pct), p = Number(dist.phrase_pct), b = Number(dist.broad_pct);
        if ([e, p, b].every((n) => Number.isFinite(n)) && e + p + b > 0) {
            counts.exact = Math.round((e / 100) * total);
            counts.phrase = Math.round((p / 100) * total);
            counts.broad = Math.max(0, total - counts.exact - counts.phrase);
            return counts;
        }
    }
    kws.forEach((k) => { counts[k.match_type] = (counts[k.match_type] || 0) + 1; });
    return counts;
}
